import { useQuery } from 'react-query';
import { RestError } from '@azure/core-http';
import {
  KnownSearchRequestDtoTypesItem,
  SearchRequestDtoTypesItem,
  SearchResponseDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useApi } from '../api/api.context';
import { appInsights } from '../telemetry/app-insights';
import { QueryKeys } from './query-keys.constants';

/** Term length bounds enforced by the server; mirrored client-side to avoid a doomed round trip. */
export const MIN_TERM_LENGTH = 3;
export const MAX_TERM_LENGTH = 50;

/** Per-type fetch size. Backs the "show more" reveal without over-fetching (server default is 50). */
export const SEARCH_LIMIT = 20;

/**
 * How long a completed search's results stay warm across a drawer close/reopen. Within the window the
 * results are kept (with the inputs cleared) so an accidental close doesn't lose them; past it, reopening
 * gives a clean slate. In-memory only — a page reload always resets.
 */
export const RESULTS_STALE_MS = 15 * 60 * 1000;

/** Cheap indexes searched by default (mirrors the server's own default set). */
export const DEFAULT_SEARCH_TYPES: SearchRequestDtoTypesItem[] = [
  KnownSearchRequestDtoTypesItem.Project,
  KnownSearchRequestDtoTypesItem.UserOrAccount,
  KnownSearchRequestDtoTypesItem.Hub
];

/** Default set plus the heavy delivery/order indexes, added when "Include deliveries" is checked. */
export const ALL_SEARCH_TYPES: SearchRequestDtoTypesItem[] = [
  ...DEFAULT_SEARCH_TYPES,
  KnownSearchRequestDtoTypesItem.Delivery,
  KnownSearchRequestDtoTypesItem.Order
];

export interface SearchSubmission {
  term: string;
  types: SearchRequestDtoTypesItem[];
}

/**
 * A "GF-…" delivery signature / reference (e.g. `GF-8QK0-3ZLN`): starts with `GF-` and is longer
 * than 8 chars. Searching one implies the user wants deliveries included.
 */
export function isDeliverySignatureTerm(term: string): boolean {
  const trimmed = term.trim();
  return trimmed.length > 8 && trimmed.toUpperCase().startsWith('GF-');
}

/**
 * Runs a universal search. Driven by an explicit submission (button / Enter) rather than
 * debounced typing — the query only fires once `submission` is set, and re-keys on a new
 * submission so a stale response is replaced. No `projectIDs` are sent: search is always
 * cross-project over all accessible projects.
 */
export function useSearchQuery(submission?: SearchSubmission) {
  const api = useApi();

  return useQuery<SearchResponseDto, RestError>(
    [QueryKeys.SEARCH, submission?.term, submission?.types],
    () => runSearch(api, submission!),
    {
      enabled: !!submission && submission.term.length >= MIN_TERM_LENGTH,
      keepPreviousData: false
    }
  );
}

async function runSearch(api: ReturnType<typeof useApi>, submission: SearchSubmission): Promise<SearchResponseDto> {
  const { term, types } = submission;
  const isFullSearch =
    types.includes(KnownSearchRequestDtoTypesItem.Delivery) || types.includes(KnownSearchRequestDtoTypesItem.Order);
  // Attach the raw term for the notable cases only — short terms and full (delivery/order) searches —
  // to gather internal usage data on what people actually search.
  const captureTerm = term.length < 4 || isFullSearch;
  const start = performance.now();

  try {
    const response = await api.searchController.search({ term, types, limit: SEARCH_LIMIT });
    const resultCount = response.results?.length ?? 0;
    appInsights.trackEvent({
      name: 'UniversalSearch',
      properties: {
        isFullSearch,
        deliverySignature: String(isDeliverySignatureTerm(term)),
        outcome: resultCount ? 'success' : 'empty',
        termLength: String(term.length),
        typeCount: String(types.length),
        ...(captureTerm ? { term } : {})
      },
      measurements: { durationMs: performance.now() - start, resultCount }
    });
    return response;
  } catch (error) {
    appInsights.trackException({ exception: error as Error }, { feature: 'UniversalSearch', isFullSearch });
    throw error;
  }
}
